// Per-route access (Phase: admin/role-based-access §2). See Phases/admin/PHASE-ROLE-BASED-ACCESS.md.
//
// Routes declare `handle: { access }` alongside the `title` / `width` / `navTrail` they already
// carry, and the deepest matched handle wins — the same idiom `usePageTitle` and `AppLayout` use.
// A parent's `access` therefore covers its children, which is how `/admin/*` and `/pe/*` are
// already grouped.
//
// **Additive by construction.** A route that declares no `access` resolves to `null` and the layout
// takes exactly the path it took before this existed. `RoleRoute` is untouched and still runs; where
// both apply, both must pass, because this layer can only ever subtract.
import { useMatches } from 'react-router-dom';
import { useAccess } from '@/hooks/useAccess';
import { isTier, PERMISSION_MIN_TIER, type AccessSpec, type Tier } from '@/auth/tiers';
import { featureRolledOutTo, type FeatureKey } from '@/auth/features';

/** How a refusal renders. */
export type DenialMode =
  /** Bare `NotFoundPage`, no shell — indistinguishable from a typo'd URL. */
  | 'notFound'
  /** `ForbiddenPage` inside the shell — actionable ("contact your organization admin"). */
  | 'forbidden';

/** Everything a route may put in `handle`. Superset of what AppLayout consumed before §2. */
export interface RouteHandle {
  title?: string;
  width?: string;
  navTrail?: string;
  /** A tier (`'user' | 'org' | 'platform'`) or a raw `PermissionKey`. */
  access?: AccessSpec;
  /** Tiers excluded despite `access` passing. Cosmetic until the backend privacy phases land. */
  deny?: Tier[];
  /** Override the default denial for this route. */
  denial?: DenialMode;
  /**
   * Rollout gate (§3c), checked IN ADDITION to `access`. `access` says who will be entitled once
   * the feature ships; this says whether it has shipped to them yet. A route held back by its
   * rollout always denies with `notFound` — an unreleased feature should not advertise itself.
   */
  feature?: FeatureKey;
}

/**
 * Default denial mode: **platform-tier pages 404**, so a tenant cannot tell a platform page from a
 * typo; everything else 403, which is actionable for a member.
 *
 * Raw capability keys are mapped through `PERMISSION_MIN_TIER`, so `access: 'pe:admin'` (the
 * staff operator console) 404s like `access: 'platform'` does rather than falling through to 403.
 */
export function defaultDenial(spec: AccessSpec): DenialMode {
  const minTier = isTier(spec) ? spec : PERMISSION_MIN_TIER[spec];
  return minTier === 'platform' ? 'notFound' : 'forbidden';
}

export interface ResolvedRouteAccess {
  access: AccessSpec;
  deny?: Tier[];
  denial: DenialMode;
  feature?: FeatureKey;
}

/**
 * Resolve the effective access declaration from a matched-route chain (root → leaf).
 *
 * `access`/`deny` and `denial` are resolved independently, each deepest-wins, so a child can
 * override just the denial mode of a gate its parent declares.
 */
export function resolveRouteAccess(
  handles: readonly (RouteHandle | undefined)[],
): ResolvedRouteAccess | null {
  let found: { access: AccessSpec; deny?: Tier[] } | null = null;
  let denial: DenialMode | undefined;
  let feature: FeatureKey | undefined;

  for (let i = handles.length - 1; i >= 0; i -= 1) {
    const handle = handles[i];
    if (!handle) continue;
    if (!denial && handle.denial) denial = handle.denial;
    if (!feature && handle.feature) feature = handle.feature;
    if (!found && handle.access) found = { access: handle.access, deny: handle.deny };
  }

  if (!found) return null;
  return { ...found, denial: denial ?? defaultDenial(found.access), feature };
}

/**
 * The denial mode for the current route, or `null` when access is granted **or** the route declares
 * no `access` at all. Layouts call this and short-circuit before rendering `<Outlet />`.
 */
export function useRouteAccessDenial(): DenialMode | null {
  const matches = useMatches();
  const { allows, tier } = useAccess();

  const resolved = resolveRouteAccess(matches.map((m) => m.handle as RouteHandle | undefined));
  if (!resolved) return null;

  // Entitlement first, then rollout. A route held back by its rollout 404s regardless of the
  // entitlement's own denial mode: an unreleased feature must not announce that it exists.
  if (!allows(resolved.access, resolved.deny)) return resolved.denial;
  if (resolved.feature && !featureRolledOutTo(resolved.feature, tier)) return 'notFound';
  return null;
}
